import { all, call, fork, put, take, race, select } from "redux-saga/effects";
import { delay } from "redux-saga";

import { MessageRoomPartialID } from "../../domain/model/entity";
import { MessageRoom } from "../../domain/model/message_room";
import { Message } from "../../domain/model/message";
import { Project } from "../../domain/model/project";
import { IMessageRepository } from "../../domain/repository/message_repository";
import { IMessageRoomRepository } from "../../domain/repository/message_room_repository";
import {
  TCompanyIDWithProjectID,
  TCompanyWithProjectID,
  ICompanyRepository,
} from "../../domain/repository/company_repository";
import { IProjectRepository } from "../../domain/repository/project_repository";

import { createApiClient, IApiClient } from "../../infra/api/client";
import { createMessageRoomAPI } from "../../infra/api/service/message_room_api";
import { createMessageAPI } from "../../infra/api/service/message_api";
import { createCompanyAPI } from "../../infra/api/service/company_api";
import { createProjectAPI } from "../../infra/api/service/project_api";

import {
  CustomerMessageRoomsActionType,
  createCustomerMessageRoomsActionCreator,
  ICustomerMessageRoomsActionCreator,
} from "../action/customer_message_rooms_action";
import { handleErrorForHandler } from "./handleErrorForHandler";
import { StartPollingMessageRoomsByCustomerResponse } from "../response/message_room_response";
import { AppState } from "../store/app_state";
import { CustomerMessageRoomsState } from "../store/customer_message_rooms_state";
import { IContractRepository } from "src/domain/repository/contract_repository";
import { Contract } from "src/domain/model/contract";
import { createContractAPI } from "src/infra/api/service/contract_api";

const apiClient: IApiClient = createApiClient();
const messageRoomRepository: IMessageRoomRepository =
  createMessageRoomAPI(apiClient);
const messageRepository: IMessageRepository = createMessageAPI(apiClient);
const companyRepository: ICompanyRepository = createCompanyAPI(apiClient);
const projectRepository: IProjectRepository = createProjectAPI(apiClient);
const contractRepository: IContractRepository = createContractAPI(apiClient);
const actionCreator: ICustomerMessageRoomsActionCreator =
  createCustomerMessageRoomsActionCreator();

// polling
function* pollTask() {
  while (true) {
    try {
      const messageRooms: MessageRoom[] = yield call(getAllMessageRooms);
      const contractCount: number | Long = yield call(getCountByCustomer);
      const contracts: Contract[] = yield call(
        getAllByCustomer,
        1,
        contractCount
      );
      let projectIds: string[];
      let projects: Project[];
      let messageRoomPartialIDS: MessageRoomPartialID[];
      let latestMessages: Message[];
      const customerMessageRoomsSelector = (state: AppState) =>
        state.customerMessageRoomsReducer;
      const _customerMessageRoomsState: CustomerMessageRoomsState =
        yield select(customerMessageRoomsSelector);
      const _initialMessageRoom = _customerMessageRoomsState.initialMessageRoom;
      [projectIds, messageRoomPartialIDS] = yield all([
        call(createProjectIds, messageRooms),
        call(createMessageRoomPartialIds, messageRooms),
      ]);
      [projects, latestMessages] = yield all([
        call(getAllByProjectIds, projectIds),
        call(getAllNewestByRoomIDs, messageRoomPartialIDS),
      ]);
      const companyIDWithProjectIDs: TCompanyIDWithProjectID[] = yield call(
        createCompanyIdWithProjectIds,
        projects
      );
      const companyWithProjectIDs: TCompanyWithProjectID[] = yield call(
        getMaskCompanies,
        companyIDWithProjectIDs
      );
      const firstMessageRoom = messageRooms.length > 0 ? messageRooms[0] : null;
      const initialMessageRoom: MessageRoom | null = _initialMessageRoom
        ? _initialMessageRoom
        : firstMessageRoom;
      const res: StartPollingMessageRoomsByCustomerResponse =
        new StartPollingMessageRoomsByCustomerResponse();
      res.messageRooms = messageRooms;
      res.companyWithProjectIDs = companyWithProjectIDs;
      res.projects = projects;
      res.latestMessages = latestMessages;
      res.initialMessageRoom = initialMessageRoom;
      res.contracts = contracts;

      yield put(
        actionCreator.callbackStartPollingMessageRoomsAction(true, res)
      );
      yield call(delay, 30000);
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreator.callbackStartPollingMessageRoomsAction(
          false,
          undefined,
          error.toString()
        )
      );
      yield put(actionCreator.requestStopPollingMessageRoomsAction()); // error時に停止
    }
  }
}

function* handlePollTaskWatcherInCustomerMessageRooms() {
  while (true) {
    yield take(
      CustomerMessageRoomsActionType.REQUEST_START_POLLING_MESSAGE_ROOMS
    );
    yield race({
      pollTask: call(pollTask),
      stopTask: take(
        CustomerMessageRoomsActionType.REQUEST_STOP_POLLING_MESSAGE_ROOMS
      ),
    });
  }
}

const createCompanyIdWithProjectIds = (
  projects: Project[]
): TCompanyIDWithProjectID[] => {
  return projects.map((item: Project) => {
    const companyIDWithProjectID: TCompanyIDWithProjectID = {
      companyID: item.companyID,
      projectID: item.id,
    };
    return companyIDWithProjectID;
  });
};

const createProjectIds = (messageRooms: MessageRoom[]): string[] => {
  return messageRooms.map((item: MessageRoom) => {
    return item.projectID;
  });
};

const createMessageRoomPartialIds = (
  messageRooms: MessageRoom[]
): MessageRoomPartialID[] => {
  return messageRooms.map((item: MessageRoom) => {
    return new MessageRoomPartialID(item.projectID, item.companyID);
  });
};

const getAllMessageRooms = (): Promise<MessageRoom[]> => {
  return messageRoomRepository.getAllByCustomer(1, 1000);
};

const getMaskCompanies = (
  ids: TCompanyIDWithProjectID[]
): Promise<TCompanyWithProjectID[]> => {
  return companyRepository.getAllByIDsWithMask(ids);
};

const getAllByProjectIds = (projectIds: string[]): Promise<Project[]> => {
  return projectRepository.getAllByIDs(projectIds);
};

const getAllNewestByRoomIDs = (
  messageRoomIds: MessageRoomPartialID[]
): Promise<Message[]> => {
  return messageRepository.getAllNewestByRooms(messageRoomIds);
};

const getCountByCustomer = (): Promise<number | Long> => {
  return contractRepository.getCount();
};

const getAllByCustomer = (
  page: number,
  offset: number
): Promise<Contract[]> => {
  return contractRepository.getAll(page, offset);
};

export { handlePollTaskWatcherInCustomerMessageRooms };



// WEBPACK FOOTER //
// ./src/presentation/handler/customer_message_rooms_handler.ts